import { useEffect, useMemo, useState } from "react"
import api from "../services/api"
import ProfileSkeleton from "../modules/skeleton/ProfileSkeleton"
import { Helmet } from "react-helmet"
import Swal from "sweetalert2"
import { UploadCloud } from "lucide-react"
import { auth } from "../auth/auth"

const fallbackAvatar =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="160" height="160">
      <rect width="100%" height="100%" fill="#e5e7eb"/>
      <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
        font-family="Arial" font-size="20" fill="#111827">Avatar</text>
    </svg>
  `)

export default function Profile() {
  const [user, setUser] = useState(auth.getUser())
  const [name, setName] = useState(user?.name || "")
  const [loading, setLoading] = useState(false)
  const [loadingPage, setLoadingPage] = useState(true)

  const [file, setFile] = useState(null)
  const preview = useMemo(() => (file ? URL.createObjectURL(file) : ""), [file])

  // Change Password states
  const [currentPassword, setCurrentPassword] = useState("")
  const [newPassword, setNewPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")

  const changePassword = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      return Swal.fire({
        icon: "warning",
        text: "All password fields are required",
      })
    }

    if (newPassword !== confirmPassword) {
      return Swal.fire({
        icon: "error",
        text: "Passwords do not match",
      })
    }

    if (newPassword.length < 6) {
      return Swal.fire({
        icon: "warning",
        text: "Password must be at least 6 characters",
      })
    }

    try {
      setLoading(true)

      await api.put("/api/auth/change-password", {
        currentPassword,
        newPassword,
      })

      setCurrentPassword("")
      setNewPassword("")
      setConfirmPassword("")

      Swal.fire({
        icon: "success",
        title: "Password updated",
        text: "Your password has been changed successfully.",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (e) {
      Swal.fire({
        icon: "error",
        text: e?.response?.data?.message || "Current password is incorrect",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const run = async () => {
      const token = auth.getToken()
      if (!token) return

      try {
        const { data } = await api.get("/api/profile/me")
        setUser(data.user)
        setName(data.user?.name || "")
        auth.setUser(data.user)
      } catch (e) {
        console.error(e)
      } finally {
        setLoadingPage(false)
      }
    }

    run()
  }, [api])

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview)
    }
  }, [preview])

  const saveName = async () => {
    Swal.fire({
      icon: "success",
      text: "Successfully Changed!",
      timer: 1200,
      showConfirmButton: false,
    })
    try {
      const { data } = await api.put("/api/profile/me", { name })
      setUser(data.user)
      auth.setUser(data.user)
    } catch (e) {
      alert(e?.response?.data?.message || e.message || "Update failed")
    } finally {
      setLoading(false)
    }
  }

  const uploadAvatar = async () => {
    if (!file) {
      Swal.fire({
        icon: "info",
        title: "No file selected",
        text: "Choose an image first.",
        confirmButtonColor: "#f97316",
      })
      return
    }
    // ✅ loading modal
    Swal.fire({
      title: "Uploading photo...",
      text: "Please wait",
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    })

    setLoading(true)
    try {
      const form = new FormData()
      form.append("avatar", file)

      const { data } = await api.put("/api/profile/me/avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      })

      setUser(data.user)
      auth.setUser(data.user)
      setFile(null)

      Swal.close()
      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text: "Your profile photo has been updated.",
        confirmButtonColor: "#f97316",
        timer: 1200,
        showConfirmButton: false,
      })
    } catch (e) {
      Swal.close()
      Swal.fire({
        icon: "error",
        title: "Upload failed",
        text: e?.response?.data?.message || e.message || "Upload failed",
        confirmButtonColor: "#f97316",
      })
    } finally {
      setLoading(false)
    }
  }

  if (loadingPage) {
    return <ProfileSkeleton />
  }
  return (
    <>
      <Helmet>
        <title>Profile - Stay Suite</title>
      </Helmet>
      <div className="w-full mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-4 sm:py-6">
        {/* Payments-like header */}
        <h1 className="text-2xl font-bold mb-2">Profile</h1>
        <p className="pb-5 text-gray-600 italic text-balance text-sm">
          Update your personal details and profile photo.
        </p>

        {/* Main card like Payments */}
        <div className="rounded-lg border border-gray-200 bg-white">
          {/* Top bar like Payments filters row */}
          <div className="flex flex-col gap-3 p-4 sm:p-5 border-b border-gray-200 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-center gap-4">
              <img
                className="w-14 h-14 rounded-full object-cover border border-gray-200 bg-gray-50"
                src={preview || user?.avatarUrl || fallbackAvatar}
                alt="avatar"
              />

              <div className="min-w-0">
                <div className="text-sm text-gray-500">Signed in as</div>
                <div className="font-medium text-gray-900 truncate">
                  {user?.email || "—"}
                </div>
                <div className="text-sm text-gray-500">
                  Role:{" "}
                  <span className="font-medium text-gray-700">
                    {user?.role || "—"}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
              {/* Left: Photo upload (receipt-style) */}
              <div className="lg:col-span-5">
                <h2 className="text-sm font-semibold text-gray-800 mb-2">
                  Profile Photo
                </h2>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <label
                    className="flex flex-col items-center justify-center w-full
                    h-32 border-2 border-dashed border-gray-300 rounded-xl
                    cursor-pointer bg-gray-50 hover:bg-gray-100 hover:border-blue-400 transition"
                  >
                    <UploadCloud className="w-12 h-12 pb-2" />
                    <p className="text-xs text-gray-600">
                      <span className="font-semibold text-blue-600">
                        Click to upload{" "}
                      </span>
                      or drag & drop
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">
                      PNG, JPG, WEBP - Max 5MB
                    </p>

                    <input
                      type="file"
                      className="hidden"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => setFile(e.target.files?.[0] || null)}
                    />
                  </label>

                  <div className="mt-3 flex items-center justify-between gap-2">
                    {file ? (
                      <p className="text-xs text-gray-500">
                        Selected:{" "}
                        <span className="font-medium">{file.name}</span>
                      </p>
                    ) : (
                      <p className="text-xs text-gray-400">No file selected</p>
                    )}

                    <button
                      type="button"
                      onClick={uploadAvatar}
                      disabled={loading || !file}
                      className="px-4 py-2 rounded-lg text-sm font-semibold
                      bg-orange-600 text-white hover:bg-orange-700
                      disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {loading ? "Uploading..." : "Upload"}
                    </button>
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    Max 5MB. JPG/PNG/WebP.
                  </p>
                </div>
              </div>

              {/* Right: Name edit (Payments modal input style) */}
              <div className="lg:col-span-7">
                <h2 className="text-sm font-semibold text-gray-800 mb-2">
                  Basic Information
                </h2>

                <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5">
                  <div className="space-y-1">
                    <label className="text-sm font-medium text-gray-700">
                      Name
                    </label>

                    <input
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
                      focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                    />

                    <p className="text-xs text-gray-500">
                      This name will be shown across the admin dashboard.
                    </p>
                  </div>

                  {/* Footer bar like Payments pagination bar */}
                  <div className="mt-4 flex items-center justify-between px-4 py-3 border rounded-lg bg-white border-amber-400">
                    <p className="text-sm text-gray-600">
                      Make sure your details are accurate before saving.
                    </p>

                    <button
                      type="button"
                      onClick={saveName}
                      disabled={loading}
                      className="px-4 py-2 rounded-lg text-sm font-semibold
                      bg-orange-600 text-white hover:bg-orange-700
                      disabled:opacity-60 disabled:cursor-not-allowed transition"
                    >
                      {loading ? "Saving..." : "Save"}
                    </button>
                  </div>
                </div>

                <div className="text-center">
                  {user?.avatarUrl ? (
                    <button
                      type="button"
                      onClick={() => window.open(user.avatarUrl, "_blank")}
                      className="text-xs text-blue-600  mt-3 inline-block hover:underline"
                    >
                      View current photo
                    </button>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
          {/* Change Password */}
          <div className="p-4 mt-6">
            <h2 className="text-sm font-semibold text-gray-800 mb-2">
              Change Password
            </h2>

            <div className="rounded-lg border border-gray-200 bg-white p-4 sm:p-5 space-y-4">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Current Password
                </label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="Please input your current password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  New Password
                </label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="Please input your new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">
                  Confirm New Password
                </label>
                <input
                  type="password"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm
 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition"
                  placeholder="Please input your confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                />
              </div>

              <div className="flex justify-end">
                <button
                  onClick={changePassword}
                  disabled={loading}
                  className="px-4 py-2 rounded-lg text-sm font-semibold
        bg-orange-600 text-white hover:bg-orange-700
        disabled:opacity-60"
                >
                  {loading ? "Updating..." : "Change Password"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
